import { isString } from 'es-toolkit'

import { isObject } from '.'

// Define types for class name arguments
type ClassValue = string | boolean | null | undefined | Record<string, unknown> | ClassValue[]

/**
 * Combines multiple class names into a single space-separated string.
 * Useful for conditional class application and dynamic styling.
 *
 * @param args - Any number of arguments that can be class names, objects, arrays, or falsy values
 * @returns A space-separated string of class names
 *
 * @example Basic usage
 * ```tsx
 * clsx('foo', 'bar'); // => 'foo bar'
 * clsx('foo', undefined, 'bar', null); // => 'foo bar'
 * ```
 *
 * @example Conditional classes with boolean expressions
 * ```tsx
 * clsx('btn', isActive && 'btn-active'); // => 'btn btn-active' or 'btn'
 * clsx('menu-item', isCurrent && 'current', isDisabled && 'disabled');
 * ```
 *
 * @example Object syntax for multiple conditions
 * ```tsx
 * clsx('btn', {
 *   'btn-primary': type === 'primary',
 *   'btn-danger': type === 'danger',
 *   'btn-disabled': isDisabled
 * });
 * ```
 *
 * @example With CSS Modules
 * ```tsx
 * import styles from './Button.module.css';
 *
 * clsx(
 *   styles.button,
 *   size === 'large' && styles.buttonLarge,
 *   { [styles.primary]: variant === 'primary' }
 * );
 * ```
 *
 * @example Mixed usage with arrays
 * ```tsx
 * clsx(
 *   'header',
 *   [
 *     isSticky && 'header-sticky',
 *     { 'header-transparent': isTransparent }
 *   ]
 * );
 * ```
 *
 * @example Common usage in React components
 * ```tsx
 * <button
 *   className={clsx(
 *     styles.button,
 *     disabled && styles.disabled,
 *     {
 *       [styles.primary]: variant === 'primary',
 *       [styles.outline]: variant === 'outline',
 *     }
 *   )}
 * >
 *   {children}
 * </button>
 * ```
 */
export default function clsx(...args: ClassValue[]): string {
  const classes: string[] = []

  args.forEach(arg => {
    if (isString(arg)) {
      classes.push(arg)
    } else if (Array.isArray(arg)) {
      classes.push(clsx(...arg))
    } else if (isObject(arg)) {
      const obj = arg as Record<string, unknown>
      Object.keys(obj).forEach(key => {
        if (obj[key]) {
          classes.push(key)
        }
      })
    }
  })

  return classes.join(' ')
}
